import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import {
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map
} from 'rxjs';

import { PaginationMetaInterface } from '../../../core/interfaces';
import { AuthService } from '../../auth/services/auth.service';
import { AuthStatusEnum } from '../../auth/enums';
import { NotificationPriorityEnum, NotificationStatusEnum } from '../enums/notification-type.enum';
import {
  NotificationInterface,
  NotificationReadAllEventInterface
} from '../interfaces/notification.interface';
import { NotificationsApiService } from '../services/notifications-api.service';
import { NotificationSocketService } from '../services/notification-socket.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsFacade implements OnDestroy {
  private readonly apiService = inject(NotificationsApiService);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly socketService = inject(NotificationSocketService);

  private readonly _notifications = signal<NotificationInterface[]>([]);
  private readonly _unreadCount = signal(0);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _meta = signal<PaginationMetaInterface | null>(null);

  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly meta = this._meta.asReadonly();

  readonly notifications$: Observable<NotificationInterface[]> = toObservable(this._notifications);
  readonly unreadCount$: Observable<number> = toObservable(this._unreadCount);
  readonly loading$: Observable<boolean> = toObservable(this._loading);
  readonly error$: Observable<string | null> = toObservable(this._error);
  readonly meta$: Observable<PaginationMetaInterface | null> = toObservable(this._meta);

  private readonly subscriptions = new Subscription();
  private currentPage = 1;
  private currentStatus?: NotificationStatusEnum;
  private readonly pageSize = 20;

  constructor() {
    this.listenToAuthenticationContext();
    this.listenToSocketEvents();
    this.subscriptions.add(this.authService.onLogout$.subscribe(() => this.clearContext()));
  }

  loadPage(page = 1, status = this.currentStatus): void {
    this.currentPage = page;
    this.currentStatus = status;
    this._loading.set(true);
    this._error.set(null);

    this.apiService.getNotifications(page, this.pageSize, status).subscribe({
      next: (response) => {
        this._notifications.set(response.notifications);
        this._meta.set(response.meta);
        this._loading.set(false);
      },
      error: () => {
        this._error.set('Não foi possível carregar as notificações.');
        this._loading.set(false);
      }
    });
  }

  filterByStatus(status?: NotificationStatusEnum): void {
    this.loadPage(1, status);
  }

  markAsRead(id: string): void {
    const notification = this._notifications().find((item) => item.id === id);
    if (!notification || notification.status === NotificationStatusEnum.READ) return;

    this.applyReadState(id, new Date().toISOString());
    this.apiService.markAsRead(id).subscribe({
      next: (updatedNotification) => this.mergeNotification(updatedNotification),
      error: () => this.synchronize()
    });
  }

  markAllAsRead(): void {
    if (this._unreadCount() === 0) return;
    this.applyAllReadState();
    this.apiService.markAllAsRead().subscribe({ error: () => this.synchronize() });
  }

  private listenToAuthenticationContext(): void {
    const contextSubscription = combineLatest([
      this.authService.status$,
      this.authService.currentCompany$
    ])
      .pipe(
        map(([status, company]) =>
          status === AuthStatusEnum.AUTHENTICATED ? company?.companyId ?? null : null
        ),
        distinctUntilChanged()
      )
      .subscribe((companyId) => {
        this.clearContext();
        if (!companyId) return;
        this.synchronize();
        this.socketService.connect();
      });
    this.subscriptions.add(contextSubscription);
  }

  private listenToSocketEvents(): void {
    this.subscriptions.add(
      this.socketService
        .fromEvent<NotificationInterface>('notification')
        .subscribe((notification) => this.handleNewNotification(notification))
    );
    this.subscriptions.add(
      this.socketService
        .fromEvent<NotificationInterface>('notification:read')
        .subscribe((notification) => this.mergeNotification(notification))
    );
    this.subscriptions.add(
      this.socketService
        .fromEvent<NotificationReadAllEventInterface>('notifications:read-all')
        .subscribe(() => this.applyAllReadState())
    );
    this.subscriptions.add(
      this.socketService.fromEvent<void>('socket:reconnected').subscribe(() => this.synchronize())
    );
  }

  private synchronize(): void {
    this.loadPage(this.currentPage, this.currentStatus);
    this.apiService.getUnreadCount().subscribe({
      next: ({ count }) => this._unreadCount.set(count),
      error: () => this._error.set('Não foi possível atualizar o total de notificações.')
    });
  }

  private handleNewNotification(notification: NotificationInterface): void {
    const withoutDuplicate = this._notifications().filter(
      (currentNotification) => currentNotification.id !== notification.id
    );
    if (this.currentPage === 1 && this.currentStatus !== NotificationStatusEnum.READ) {
      this._notifications.set([notification, ...withoutDuplicate].slice(0, this.pageSize));
    }
    if (notification.status === NotificationStatusEnum.UNREAD) {
      this._unreadCount.update((count) => count + 1);
    }
    this.showImportantNotification(notification);
  }

  private mergeNotification(notification: NotificationInterface): void {
    this._notifications.update((list) =>
      list.map((currentNotification) =>
        currentNotification.id === notification.id ? notification : currentNotification
      )
    );
    this.refreshUnreadCount();
  }

  private applyReadState(notificationId: string, readAt: string): void {
    this._notifications.update((list) =>
      list.map((notification) =>
        notification.id === notificationId
          ? { ...notification, readAt, status: NotificationStatusEnum.READ }
          : notification
      )
    );
    this._unreadCount.update((count) => Math.max(0, count - 1));
  }

  private applyAllReadState(): void {
    const readAt = new Date().toISOString();
    this._notifications.update((list) =>
      list.map((notification) => ({
        ...notification,
        readAt,
        status: NotificationStatusEnum.READ
      }))
    );
    this._unreadCount.set(0);
  }

  private refreshUnreadCount(): void {
    this.apiService.getUnreadCount().subscribe({
      next: ({ count }) => this._unreadCount.set(count)
    });
  }

  private showImportantNotification(notification: NotificationInterface): void {
    const importantPriorities = new Set([
      NotificationPriorityEnum.HIGH,
      NotificationPriorityEnum.URGENT,
      NotificationPriorityEnum.CRITICAL
    ]);
    if (!importantPriorities.has(notification.priority)) return;

    this.messageService.add({
      detail: notification.message,
      severity:
        notification.priority === NotificationPriorityEnum.CRITICAL ? 'error' : 'warn',
      summary: notification.title
    });
  }

  private clearContext(): void {
    this.socketService.disconnect();
    this.currentPage = 1;
    this.currentStatus = undefined;
    this._notifications.set([]);
    this._unreadCount.set(0);
    this._meta.set(null);
    this._error.set(null);
    this._loading.set(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.socketService.disconnect();
  }
}
